/**
 * @fileoverview API client service.
 * @summary Handles HTTP requests with automatic cookie management.
 * @package PlayItNow
 * @module apps/front/services/api
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { API_URL } from '../config.js'

/**
 * @summary HTTP methods.
 */
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

/**
 * @summary Request options extending native RequestInit.
 */
interface RequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  /** HTTP method to use */
  method?: HttpMethod
  /** Request body (will be JSON-serialized) */
  body?: unknown
}

/**
 * @summary API error response shape.
 */
export interface ApiError {
  /** Error code or name */
  error: string
  /** Human-readable error message */
  message: string
}

/**
 * @summary Custom error class for API errors.
 */
export class ApiException extends Error {
  /** HTTP status code */
  status: number
  /** Parsed error response body */
  data: ApiError

  constructor(status: number, data: ApiError) {
    super(data.message)
    this.name = 'ApiException'
    this.status = status
    this.data = data
  }
}

/**
 * @summary Make an API request.
 * @param endpoint API endpoint (without prefix).
 * @param options Request options.
 * @returns Response data.
 * @throws {ApiException} If the request fails.
 */
export async function apiRequest<T = unknown>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, headers = {}, ...restOptions } = options

  // Build URL
  const url = `${API_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`

  // Prepare headers
  const requestHeaders: HeadersInit =
    body !== undefined && body !== null
      ? {
          'Content-Type': 'application/json',
          ...headers,
        }
      : headers

  // Prepare fetch options
  const fetchOptions: RequestInit = {
    method,
    headers: requestHeaders,
    credentials: 'include', // Important: include cookies (httpOnly)
    ...restOptions,
  }

  // Add body if present
  if (body !== undefined && body !== null) {
    fetchOptions.body = JSON.stringify(body)
  }

  // Make request
  const response = await fetch(url, fetchOptions)

  // Handle errors
  if (!response.ok) {
    let errorData: ApiError
    try {
      errorData = await response.json()
    } catch {
      errorData = {
        error: 'UnknownError',
        message: `HTTP ${response.status}: ${response.statusText}`,
      }
    }
    throw new ApiException(response.status, errorData)
  }

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    return undefined as T
  }

  // Parse and return response
  try {
    return await response.json()
  } catch {
    return undefined as T
  }
}

/**
 * @summary GET request helper.
 * @param endpoint - API endpoint (without prefix).
 * @param options - Optional request options.
 * @returns Response data.
 */
export function get<T = unknown>(
  endpoint: string,
  options?: Omit<RequestOptions, 'method' | 'body'>,
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'GET' })
}

/**
 * @summary POST request helper.
 * @param endpoint - API endpoint (without prefix).
 * @param body - Request body.
 * @param options - Optional request options.
 * @returns Response data.
 */
export function post<T = unknown>(
  endpoint: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>,
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'POST', body })
}

/**
 * @summary PUT request helper.
 * @param endpoint - API endpoint (without prefix).
 * @param body - Request body.
 * @param options - Optional request options.
 * @returns Response data.
 */
export function put<T = unknown>(
  endpoint: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>,
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'PUT', body })
}

/**
 * @summary DELETE request helper.
 * @param endpoint - API endpoint (without prefix).
 * @param options - Optional request options.
 * @returns Response data.
 */
export function del<T = unknown>(
  endpoint: string,
  options?: Omit<RequestOptions, 'method' | 'body'>,
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'DELETE' })
}

/**
 * @summary PATCH request helper.
 * @param endpoint - API endpoint (without prefix).
 * @param body - Request body.
 * @param options - Optional request options.
 * @returns Response data.
 */
export function patch<T = unknown>(
  endpoint: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>,
): Promise<T> {
  return apiRequest<T>(endpoint, { ...options, method: 'PATCH', body })
}

/**
 * @summary OAuth providers enabled flags (from backend env).
 */
export interface OAuthProvidersResponse {
  google: boolean
  meta: boolean
}

/**
 * @summary Fetch which OAuth providers are enabled (public endpoint).
 */
export function getOAuthProviders(): Promise<OAuthProvidersResponse> {
  return get<OAuthProvidersResponse>('/auth/oauth-providers')
}
